import dayjs, { Dayjs } from 'dayjs'

import 'dayjs/locale/pt-br'
import 'dayjs/locale/es'
import 'dayjs/locale/en'

import localizedFormat from 'dayjs/plugin/localizedFormat'
import {
  localizedFormatString,
  LangFormatProps
} from './i18n/localizedFormatString'
import { getSafeLangCode } from './i18n/safeLangCode'

dayjs.extend(localizedFormat)

type DateTimeUnits =
  | 'year'
  | 'month'
  | 'week'
  | 'day'
  | 'hour'
  | 'minute'
  | 'second'

interface DateTimeOptions {
  years?: number
  months?: number
  weeks?: number
  days?: number
  hours?: number
  minutes?: number
  seconds?: number
}

interface DateObjProps {
  year: number
  month: number
  date: number
  weekday: number
  hour: number
  minute: number
  second: number
  millisecond: number
}

type ToOptions = Partial<Omit<DateObjProps, 'weekday'>>

export class SafeDate {
  private value: Dayjs = dayjs()
  private isDateValid = true
  private locale = 'pt-br'

  public static isValid(value?: Date | string | Dayjs) {
    return dayjs(value || '').isValid()
  }

  private validationHandler<T>(value: T, onInvalid: T): T {
    return this.isDateValid ? value : onInvalid
  }

  private imutableHandler(value: Dayjs, locale?: string) {
    return new SafeDate(value, locale || this.locale)
  }

  private i18nFormat(key: keyof LangFormatProps) {
    return (
      localizedFormatString[this.locale]?.[key] ||
      localizedFormatString.default[key] ||
      ''
    )
  }

  private getI18nCode(locale: string) {
    return getSafeLangCode(locale)
  }

  public get isValid(): boolean {
    return this.isDateValid
  }

  public get lang() {
    return this.locale
  }

  public get jsDate() {
    return this.value.toDate()
  }

  public get timestamp() {
    return this.validationHandler(this.jsDate.getTime(), NaN)
  }

  public get isoDate() {
    return this.validationHandler(this.value.format('YYYY-MM-DD'), '')
  }

  public get isoString() {
    return this.validationHandler(this.value.format('YYYY-MM-DDTHH:mm:ss'), '')
  }

  public get daysInMonth() {
    return this.validationHandler(this.value.daysInMonth(), 0)
  }

  public get dateObj(): DateObjProps {
    return this.validationHandler(
      {
        year: this.value.year(),
        month: this.value.month(),
        date: this.value.date(),
        weekday: this.value.day(),
        hour: this.value.hour(),
        minute: this.value.minute(),
        second: this.value.second(),
        millisecond: this.value.millisecond()
      },
      {
        year: 0,
        month: 0,
        date: 0,
        weekday: 0,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0
      }
    )
  }

  public toStartOf(unit: DateTimeUnits) {
    const newValue = this.validationHandler(
      this.value.startOf(unit),
      this.value
    )
    return this.imutableHandler(newValue)
  }

  public add({
    years = 0,
    months = 0,
    weeks = 0,
    days = 0,
    hours = 0,
    minutes = 0,
    seconds = 0
  }: DateTimeOptions) {
    const newValue = this.validationHandler(
      this.value
        .add(years, 'year')
        .add(months, 'month')
        .add(weeks, 'week')
        .add(days, 'day')
        .add(hours, 'hour')
        .add(minutes, 'minute')
        .add(seconds, 'second'),
      this.value
    )
    return this.imutableHandler(newValue)
  }

  public subtract({
    years = 0,
    months = 0,
    weeks = 0,
    days = 0,
    hours = 0,
    minutes = 0,
    seconds = 0
  }: DateTimeOptions) {
    const newValue = this.validationHandler(
      this.value
        .subtract(years, 'year')
        .subtract(months, 'month')
        .subtract(weeks, 'week')
        .subtract(days, 'day')
        .subtract(hours, 'hour')
        .subtract(minutes, 'minute')
        .subtract(seconds, 'second'),
      this.value
    )
    return this.imutableHandler(newValue)
  }

  public to(newValues: ToOptions) {
    const currentValues: Required<ToOptions> = this.dateObj

    const {
      year = currentValues.year,
      month = currentValues.month,
      date = currentValues.date,
      hour = currentValues.hour,
      minute = currentValues.minute,
      second = currentValues.second,
      millisecond = currentValues.millisecond
    } = newValues

    const newValue = this.validationHandler(
      this.value
        .year(year)
        .month(month)
        .date(date)
        .hour(hour)
        .minute(minute)
        .second(second)
        .millisecond(millisecond),
      this.value
    )

    return this.imutableHandler(newValue)
  }

  public isBefore(date: Date | string, unit?: DateTimeUnits) {
    return this.validationHandler(this.value.isBefore(date, unit), false)
  }

  public isAfter(date: Date | string, unit?: DateTimeUnits) {
    return this.validationHandler(this.value.isAfter(date, unit), false)
  }

  public isSame(date: Date | string, unit?: DateTimeUnits) {
    return this.validationHandler(this.value.isSame(date, unit), false)
  }

  public isBetween(
    startingDate: Date | string,
    endingDate: Date | string,
    unit?: DateTimeUnits
  ) {
    const isAfterStartingDate = this.validationHandler(
      this.value.isAfter(startingDate, unit),
      false
    )
    const isBeforeEndingDate = this.validationHandler(
      this.value.isBefore(endingDate, unit),
      false
    )

    const isBetweenDates =
      (isAfterStartingDate && isBeforeEndingDate) ||
      (!isAfterStartingDate && !isBeforeEndingDate)

    return this.validationHandler(isBetweenDates, false)
  }

  public getDiff(date: Date | string, unit: DateTimeUnits) {
    return this.validationHandler(this.value.diff(date, unit), 0)
  }

  public clone() {
    return this.imutableHandler(this.value)
  }

  public format(format: string) {
    return this.validationHandler(this.value.format(format), '')
  }

  public formatIntl(formatTo: keyof LangFormatProps) {
    const formatStr = this.i18nFormat(formatTo)
    return this.format(formatStr)
  }

  public setLocale(locale: string) {
    const safeLocale = this.getI18nCode(locale)
    return this.imutableHandler(this.value, safeLocale)
  }

  constructor(pValue?: Date | string | Dayjs, locale = 'pt-br') {
    this.locale = this.getI18nCode(locale)
    this.value = dayjs(pValue).locale(this.locale)
    this.isDateValid = this.value.isValid()

    if (!this.isDateValid) {
      console.error('Invalid date:', pValue)
    }
  }
}
